import type { ReactNode } from 'react';
import { Box, CardActionArea, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { MessageKey } from '../i18n';
import { TranslatedText } from './TranslatedText';

interface DashboardActionCardProps {
  icon: ReactNode;
  onClick: () => void;
  subtitle: MessageKey;
  title: MessageKey;
}

const ActionCard = styled(CardActionArea)(({ theme }) => {
  const palette = theme.vars?.palette ?? theme.palette;
  const surface = palette.surface ?? {
    panelBorder: palette.divider,
    shadow: 'none',
  };

  return {
    display: 'block',
    minHeight: 300,
    padding: theme.spacing(3),
    borderRadius: theme.shape.card,
    border: `1px solid ${surface.panelBorder}`,
    background: palette.background.paper,
    boxShadow: surface.shadow,
    transition: 'border-color 160ms ease, transform 160ms ease',
    '&:hover': {
      borderColor: palette.primary.light,
      transform: 'translateY(-2px)',
    },
    '& .MuiTouchRipple-root': {
      borderRadius: theme.shape.card,
    },
    [theme.breakpoints.down('sm')]: {
      minHeight: 260,
      padding: theme.spacing(2.25),
    },
  };
});

const Content = styled(Stack)(({ theme }) => ({
  minHeight: '100%',
  gap: theme.spacing(1.5),
  alignItems: 'flex-start',
  justifyContent: 'space-between',
}));

const IconWrap = styled(Box)(({ theme }) => {
  const palette = theme.vars?.palette ?? theme.palette;
  const surface = palette.surface ?? {
    primarySoft: palette.action.hover,
    softStrong: palette.divider,
  };

  return {
    display: 'grid',
    width: 44,
    height: 44,
    placeItems: 'center',
    borderRadius: theme.shape.surface,
    border: `1px solid ${surface.softStrong}`,
    background: surface.primarySoft,
    color: palette.primary.main,
  };
});

const CopyStack = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(0.75),
}));

export function DashboardActionCard({
  icon,
  onClick,
  subtitle,
  title,
}: DashboardActionCardProps) {
  return (
    <ActionCard onClick={onClick}>
      <Content>
        <IconWrap>{icon}</IconWrap>
        <CopyStack>
          <TranslatedText messageKey={title} variant="h4" />
          <TranslatedText
            messageKey={subtitle}
            variant="body2"
            color="text.secondary"
          />
        </CopyStack>
      </Content>
    </ActionCard>
  );
}
